import type { ReactElement } from "react";

import { translate } from "app/filters/i18nFilter";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { FiltersChip } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { FilterChipVariant } from "app/pages/sales-intelligence/common-components/filters-panel/types/commonEnums";
import type FindInternationalVisitsFilter from "app/pages/sales-intelligence/modules/leads-search-filters/models/InternationalVisitsFilter/InternationalVisitsFilter";
import {
    formatNumber,
    formatPercent,
} from "app/pages/sales-intelligence/modules/leads-search-filters/utils/numberFormatters";
import { getFormattedRangeValue } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/rangeValueFormatters";
import { getFilterChipTranslationKey } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/translationUtils";
import CountryService from "app/services/CountryService";

const InternationalVisitsFilterResult = ({
    filter: { options },
    value,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<FindInternationalVisitsFilter>): ReactElement | null => {
    const handleRemove = (field: "internationalVisits" | "internationalVisitsShare") => {
        const updatedValues = { ...value };
        delete updatedValues[field];

        onFilterValueRemove?.(updatedValues);
        onFilterUpdate(updatedValues);
    };

    const getChipItem = (
        field: "internationalVisits" | "internationalVisitsShare",
        formatter: (value: number | string) => string,
    ) => {
        if (!value || !value[field]) {
            return null;
        }
        const fieldName = translate(getFilterChipTranslationKey(field));
        return (
            <FiltersChip
                text={`${fieldName}: ${getFormattedRangeValue(formatter, options.buckets[field], value[field])}`}
                chipStatus={onGetLifecycleStatus?.({ [field]: value[field] })}
                onClose={() => handleRemove(field)}
            />
        );
    };

    const getContryChipItem = (field: "internationalCountry") => {
        if (!value || !value[field]) {
            return null;
        }

        const country = CountryService.getCountryById(value?.internationalCountry);

        return (
            <FiltersChip
                id={value[field]}
                text={country.text}
                variant={FilterChipVariant.Country}
                chipStatus={onGetLifecycleStatus?.({ [field]: value[field] })}
                hideActionButton={!isActionEnabled}
                onClose={() => {
                    onFilterValueRemove?.();
                    onFilterUpdate();
                }}
            />
        );
    };

    if (!value) {
        return null;
    }

    return (
        <FilterResultContainer>
            {getContryChipItem("internationalCountry")}
            {getChipItem("internationalVisits", formatNumber)}
            {getChipItem("internationalVisitsShare", formatPercent)}
        </FilterResultContainer>
    );
};

export default InternationalVisitsFilterResult;
